// ABOUTME: Payment validation utilities to prevent security vulnerabilities.
// ABOUTME: Provides comprehensive validation for payment amounts, providers, and product data.

import type { ProductListBuyFragment } from "@/graphql/main/gql/graphql"
import { addLogError, addLogInfo } from "@/model/logger"

export interface PaymentValidationResult {
  isValid: boolean
  errors: string[]
}

export interface ValidatedPaymentData {
  amount: number
  paymentProviderID: string
  productID: string
  targetUserID: string
}

/**
 * Validates payment amount
 * @param amount - Payment amount to validate
 * @returns validation result
 */
export const validatePaymentAmount = (amount: number): PaymentValidationResult => {
  const errors: string[] = []

  if (isNaN(amount)) {
    errors.push("errors.paymentInvalidNumber")
    addLogInfo("Payment validation: Invalid number type", { amount, type: typeof amount })
  }

  // CRITICAL: Защита от отрицательных сумм
  if (amount < 0) {
    errors.push("errors.paymentNegativeAmount")
    addLogError("SECURITY: Negative payment amount detected", { amount })
  }

  if (amount <= 0) {
    errors.push("errors.paymentZeroAmount")
  }

  // Проверяем на слишком большие суммы (предотвращает overflow)
  // Лимит: 5 000 000 копеек = 50 000 ₽
  if (amount > 5000000) {
    errors.push("errors.paymentAmountTooLarge")
    addLogInfo("Payment validation: Amount exceeds limit", { amount })
  }

  // Проверка на разумные копейки (только 2 знака после запятой)
  if (!Number.isInteger(amount * 100)) {
    errors.push("errors.paymentTooManyDecimals")
  }

  // Проверяем на Infinity и очень маленькие числа
  if (!Number.isFinite(amount)) {
    errors.push("errors.paymentInvalidNumber")
    addLogError("SECURITY: Non-finite payment amount", { amount })
  }

  if (amount > 0 && amount < 0.01) {
    errors.push("errors.paymentAmountTooSmall")
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Validates payment provider
 * @param paymentProviders - Array of available payment providers
 * @returns validation result with selected provider ID
 */
export const validatePaymentProvider = (
  paymentProviders: readonly { id: string; isActive?: boolean }[] | undefined,
): PaymentValidationResult & { providerID?: string } => {
  const errors: string[] = []

  if (!paymentProviders || paymentProviders.length === 0) {
    errors.push("errors.noPaymentProviders")
    return { isValid: false, errors }
  }

  // Ищем активного провайдера
  const activeProvider = paymentProviders.find((provider) => provider.isActive !== false && provider.id)

  if (!activeProvider) {
    errors.push("errors.noActivePaymentProvider")
    return { isValid: false, errors }
  }

  return {
    isValid: true,
    errors: [],
    providerID: activeProvider.id,
  }
}

/**
 * Validates user ID
 * @param userID - User ID to validate
 * @returns validation result
 */
export const validateUserID = (userID: string | undefined): PaymentValidationResult => {
  const errors: string[] = []

  if (!userID) {
    errors.push("errors.userIdRequired")
  } else if (userID.trim().length === 0) {
    errors.push("errors.userIdRequired")
  } else if (!/^[a-zA-Z0-9-_]+$/.test(userID)) {
    errors.push("errors.userIdInvalid")
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Validates product data
 * @param product - Product to validate
 * @returns validation result
 */
export const validateProduct = (product: ProductListBuyFragment): PaymentValidationResult => {
  const errors: string[] = []

  if (!product.id) {
    errors.push("errors.productIdRequired")
  }

  // Runtime validation: prices is typed as Record<string, unknown> but we validate anyway for safety
  const prices = product.prices as Record<string, unknown> | null | undefined
  if (!prices || typeof prices !== "object") {
    errors.push("errors.productPricesRequired")
  } else {
    const hasValidPrice = Object.values(product.prices).some((price) => {
      const numericPrice = typeof price === "string" ? parseFloat(price) : price
      return typeof numericPrice === "number" && !isNaN(numericPrice) && numericPrice > 0
    })
    if (!hasValidPrice) {
      errors.push("errors.productNoValidPrice")
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Безопасно конвертирует строку в число с проверкой на отрицательные значения
 * @param value - Значение для конвертации
 * @returns Число или undefined если конвертация невозможна
 */
const safeParseAmount = (value: unknown): number | undefined => {
  if (typeof value === "number") {
    // Дополнительная проверка числовых значений
    if (!Number.isFinite(value) || value < 0) {
      addLogError("SECURITY: Invalid numeric amount", { value })
      return undefined
    }
    return value
  }

  if (typeof value === "string") {
    const trimmed = value.trim()

    // Проверяем на пустую строку
    if (trimmed === "") {
      return undefined
    }

    // Проверяем на наличие подозрительных символов
    if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
      addLogInfo("Payment validation: Invalid price format", { value: trimmed })
      return undefined
    }

    const parsed = parseFloat(trimmed)

    // CRITICAL: Дополнительная проверка на отрицательные значения после парсинга
    if (isNaN(parsed) || parsed < 0) {
      if (parsed < 0) {
        addLogError("SECURITY: Negative amount in string conversion", { originalValue: value, parsed })
      }
      return undefined
    }

    return parsed
  }

  addLogInfo("Payment validation: Unsupported value type", { value, type: typeof value })
  return undefined
}

/**
 * Validates and extracts payment amount from product
 * @param product - Product with prices
 * @param currency - Preferred currency (default: RUB)
 * @returns validation result with amount
 */
export const extractValidatedAmount = (
  product: ProductListBuyFragment,
  currency = "RUB",
): PaymentValidationResult & { amount?: number } => {
  const errors: string[] = []

  // Runtime validation: prices is typed as Record<string, unknown> but we validate anyway for safety
  const prices = product.prices as Record<string, unknown> | null | undefined
  if (!prices) {
    errors.push("errors.productPricesRequired")
    return { isValid: false, errors }
  }

  // Пытаемся получить цену в указанной валюте (safeParseAmount проверяет на отрицательные)
  let amount: number | undefined = safeParseAmount(product.prices[currency])

  // Если нет цены в указанной валюте, берем первую доступную
  if (amount === undefined) {
    const availablePrices = Object.entries(product.prices)
      .map(([curr, price]) => {
        const parsedPrice = safeParseAmount(price)
        return parsedPrice !== undefined ? [curr, parsedPrice] : null
      })
      .filter((entry): entry is [string, number] => entry !== null)
      .filter(([, price]) => price > 0)

    const firstPrice = availablePrices[0]
    if (!firstPrice) {
      errors.push("errors.productNoValidPrice")
      return { isValid: false, errors }
    }

    amount = firstPrice[1]
  }

  // Валидируем полученную сумму
  const amountValidation = validatePaymentAmount(amount)
  if (!amountValidation.isValid) {
    errors.push(...amountValidation.errors)
  }

  return {
    isValid: errors.length === 0,
    errors,
    amount: errors.length === 0 ? amount : undefined,
  }
}

/**
 * Comprehensive payment data validation
 * @param product - Product to purchase
 * @param userID - User making the purchase
 * @param currency - Preferred currency
 * @returns validation result with validated data
 */
export const validatePaymentData = (
  product: ProductListBuyFragment,
  userID: string | undefined,
  currency = "RUB",
): PaymentValidationResult & { validatedData?: ValidatedPaymentData } => {
  const allErrors: string[] = []

  // Валидируем все компоненты
  const productValidation = validateProduct(product)
  const userValidation = validateUserID(userID)
  const amountValidation = extractValidatedAmount(product, currency)
  const providerValidation = validatePaymentProvider(product.paymentProviders)

  // Собираем все ошибки
  allErrors.push(...productValidation.errors)
  allErrors.push(...userValidation.errors)
  allErrors.push(...amountValidation.errors)
  allErrors.push(...providerValidation.errors)

  if (allErrors.length > 0) {
    return {
      isValid: false,
      errors: allErrors,
    }
  }

  return {
    isValid: true,
    errors: [],
    validatedData: {
      amount: amountValidation.amount!,
      paymentProviderID: providerValidation.providerID!,
      productID: product.id,
      targetUserID: userID!,
    },
  }
}
